import { createSelector } from '@reduxjs/toolkit';

import { makeScopeKeyExplicit, normalizeRoleKey, RoleScopeType } from '@/domain/entities/role';
import { isFixedOrgRoleKey } from '@/lib/fixedRoles';
import type { RootState } from '@/store/root-state';

const selectOrganizationsState = (state: RootState) => state.organizations;
const selectRolesState = (state: RootState) => state.roles;
const selectUsersState = (state: RootState) => state.users;

export const selectCurrentUserProfile = createSelector(selectUsersState, (s) => s.profile);
export const selectCurrentApplicationUserId = createSelector(
  selectCurrentUserProfile,
  (profile) => profile?.applicationUserId ?? null,
);

export const selectActiveOrganizationId = createSelector(selectOrganizationsState, (s) => s.activeOrganizationId);
export const selectActiveOrganization = createSelector(
  [selectOrganizationsState, selectActiveOrganizationId],
  (s, activeOrgId) => s.items.find((o) => o.id === activeOrgId) ?? null,
);

export const selectActiveMembershipForCurrentUser = createSelector(
  [selectOrganizationsState, selectActiveOrganizationId, selectCurrentApplicationUserId],
  (s, activeOrgId, userId) => {
    if (!userId) return null;
    const memberships = s.membershipsByMemberId[userId] ?? [];
    if (memberships.length === 0) return null;
    if (!activeOrgId) return memberships[0] ?? null;
    return memberships.find((membership) => membership.id === activeOrgId) ?? memberships[0] ?? null;
  },
);

export const selectActiveRoleForCurrentUser = createSelector(
  [selectRolesState, selectActiveOrganizationId, selectActiveMembershipForCurrentUser],
  (rolesState, activeOrganizationId, activeMembership) => {
    const orgId = activeOrganizationId ?? activeMembership?.id;
    const membershipRoleKey = normalizeRoleKey(activeMembership?.roleKey);
    if (!orgId || !membershipRoleKey) return null;
    if (isFixedOrgRoleKey(membershipRoleKey)) return null;

    const scopeKey = makeScopeKeyExplicit(RoleScopeType.ORG, orgId);
    const keys = rolesState.keysByScope[scopeKey] ?? [];

    for (const key of keys) {
      const role = rolesState.byKey[key];
      if (role && normalizeRoleKey(role.SK) === membershipRoleKey) {
        return role;
      }
    }

    return null;
  },
);
